import type { Services } from '@/config/mockConfig';
import { shouldUseMocks as shouldUseMocksStatic } from '@/config/mockConfig';
import { env } from '@/env';

type MockOptions = {
  delay?: number;
  shouldUseMocksFn?: (serviceName: Services) => boolean;
};

/**
 * Generic mock helper that decides whether to use mock data or real API calls.
 *
 * @param serviceName - The name of the service (used to check mock configuration)
 * @param realFn - The real API call function
 * @param mockData - The mock data to return
 * @param options - Optional configuration
 * @param options.delay - Delay in milliseconds to simulate network latency (default: 500ms)
 * @param options.shouldUseMocksFn - Custom function to determine if mocks should be used (from useMocks hook)
 * @returns Promise with either mock data or real API response
 *
 * @example
 * ```typescript
 * // Using static configuration (fallback):
 * const getUsers = async () => {
 *   return withMockSupport(
 *     'users',
 *     () => httpPrivate.get('/users'),
 *     mockUsersData,
 *     { delay: 300 }
 *   );
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Using feature flags (recommended in hooks):
 * const useUsersService = () => {
 *   const { shouldUseMocks } = useMocks();
 *
 *   const getUsers = async () => {
 *     return withMockSupport(
 *       'users',
 *       () => httpPrivate.get('/users'),
 *       mockUsersData,
 *       { delay: 300, shouldUseMocksFn: shouldUseMocks }
 *     );
 *   };
 * };
 * ```
 */
export async function withMockSupport<T>(
  serviceName: Services,
  realFn: () => Promise<T>,
  mockData: T,
  options?: MockOptions,
): Promise<T> {
  const shouldUseMocksFn = options?.shouldUseMocksFn ?? shouldUseMocksStatic;
  const useMocks = shouldUseMocksFn(serviceName);

  if (useMocks && env.VITE_NODE_ENV !== 'production') {
    // Simulate network delay
    const delay = options?.delay ?? 500;
    await new Promise(resolve => setTimeout(resolve, delay));
    return mockData;
  }

  return realFn();
}

/**
 * Alternative helper that takes a mock function instead of mock data.
 * Useful when you need to generate dynamic mock data.
 *
 * @param serviceName - The name of the service (used to check mock configuration)
 * @param realFn - The real API call function
 * @param mockFn - Function that returns mock data
 * @param options - Optional configuration
 * @param options.delay - Delay in milliseconds to simulate network latency (default: 500ms)
 * @param options.shouldUseMocksFn - Custom function to determine if mocks should be used (from useMocks hook)
 * @returns Promise with either mock data or real API response
 *
 * @example
 * ```typescript
 * // Using static configuration (fallback):
 * const getUser = async (id: string) => {
 *   return withMockSupportFn(
 *     'users',
 *     () => httpPrivate.get(`/users/${id}`),
 *     () => generateMockUser(id),
 *     { delay: 300 }
 *   );
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Using feature flags (recommended in hooks):
 * const useUsersService = () => {
 *   const { shouldUseMocks } = useMocks();
 *
 *   const getUser = async (id: string) => {
 *     return withMockSupportFn(
 *       'users',
 *       () => httpPrivate.get(`/users/${id}`),
 *       () => generateMockUser(id),
 *       { delay: 300, shouldUseMocksFn: shouldUseMocks }
 *     );
 *   };
 * };
 * ```
 */
export async function withMockSupportFn<T>(
  serviceName: Services,
  realFn: () => Promise<T>,
  mockFn: () => T | Promise<T>,
  options?: MockOptions,
): Promise<T> {
  const shouldUseMocksFn = options?.shouldUseMocksFn ?? shouldUseMocksStatic;
  const useMocks = shouldUseMocksFn(serviceName);

  if (useMocks && env.VITE_NODE_ENV !== 'production') {
    // Simulate network delay
    const delay = options?.delay ?? 500;
    await new Promise(resolve => setTimeout(resolve, delay));
    return mockFn();
  }

  return realFn();
}
